import React, { useState, useEffect, useContext } from 'react'
import { IDiamond } from 'types/diamond.types'
import Image from 'next/image'
import { Rotate360 } from '@components/icons'
import {
  transformedVideoUrl,
  hasValidVideoUrlFormat,
} from '@components/yadav/ui/DiamondViewSlider/helper'
import { LoadingDots } from '@components/ui'
import playIcon from './play-icon.png'
import axios from 'axios'
import images from './images.json'
import cn from 'clsx'
import DiamondAdditionalDetails from './DiamondAdditionalDetails'
import { AlertContext } from 'context/alertContext'

interface DiamondPreviewProps {
  diamond: IDiamond
  className?: string
  active: boolean
  previewed: boolean
}
const DiamondPreview: React.FC<DiamondPreviewProps> = ({
  diamond,
  className,
  active,
  previewed,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [showCover, setShowCover] = useState(true)
  const [isIframeAvailable, setIsIframeAvailable] = useState(false)
  const [playVideo, setPlayVideo] = useState(false)
  const [videoStatus, setVideoStatus] = useState<
    'CHECKING' | 'VALID' | 'NOT_VALID'
  >('CHECKING')
  const { setShowMessage } = useContext(AlertContext)

  const getDefaultShapeImg = (shape: string | null) => {
    const item = images.find(
      (i) => i.shape.toUpperCase() === shape?.toUpperCase()
    )
    return item?.shapeImg
  }

  const placeholderImage =
    getDefaultShapeImg(diamond.shape) ||
    process.env.NEXT_PUBLIC_SPREE_PRODUCT_PLACEHOLDER_IMAGE_URL ||
    '/product-img-placeholder.svg'

  // Set real diamond image (or sample) as "imageSrc"
  const [imageSrc, setImageSrc] = useState(
    diamond && diamond.photourl1_local
      ? diamond.photourl1_local
      : placeholderImage
  )

  // Check video URL
  useEffect(() => {
    if (previewed && diamond.videourl) {
      axios
        .get<{ isValid: boolean }>('/api/checkUrlValidity', {
          params: { url: diamond.videourl },
        })
        .then((response) =>
          response.data.isValid
            ? setVideoStatus('VALID')
            : setVideoStatus('NOT_VALID')
        )
        .catch(() => setVideoStatus('NOT_VALID'))
    }
  }, [previewed, diamond.videourl])

  // Play the video if the iframe has fully loaded
  useEffect(() => {
    if (isLoading && isIframeAvailable) {
      setPlayVideo(true)
      setIsLoading(false)
    }
  }, [isLoading, isIframeAvailable])

  useEffect(() => {
    if (playVideo && videoStatus === 'NOT_VALID') {
      setShowMessage({
        show: true,
        message:
          'An error occurred while trying to play the video. Please try again later.',
        type: 'Error',
      })
      setPlayVideo(false)
    }
  }, [playVideo, setShowMessage, videoStatus])

  return (
    <div
      className={cn(
        'flex flex-row items-center justify-start gap-0 md:gap-[50px] border-t p-2 md:p-6',
        {
          hidden: !active,
        }
      )}
    >
      <div className="relative basis-[200px] md:basis-[330px] md:h-[260px] bg-accent-2">
        {/* Show real image (or sample) if diamond has no video, or video URL is not valid */}
        {(!diamond.videourl || !hasValidVideoUrlFormat(diamond.videourl)) && (
          <div className="absolute min-w-[210px] min-h-[210px] -translate-y-[6.5rem] scale-[0.6] md:scale-100 -translate-x-6 md:translate-y-0 md:translate-x-0 top-[0px] md:top-[25px] left-0 md:left-[60px] z-[3] md:w-auto md:h-auto">
            <Image
              src={imageSrc}
              alt="Image of diamond"
              priority
              width={210}
              height={210}
              objectFit="cover"
              onError={() => setImageSrc(placeholderImage)}
              placeholder="blur"
              blurDataURL="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
              className=""
            />
          </div>
        )}
        {/* Show diamond image cover if it has a valid video URL */}
        {diamond.videourl &&
          hasValidVideoUrlFormat(diamond.videourl) &&
          showCover &&
          !playVideo && (
            <div className="absolute md:w-auto md:h-auto min-w-[210px] min-h-[210px] scale-[0.6] md:scale-100 -translate-y-[6.8rem] -translate-x-6 md:translate-x-0 md:translate-y-0 md:top-[25px] md:left-[60px] z-[3]">
              <Image
                src={imageSrc}
                alt="Image of diamond"
                priority
                width={210}
                height={210}
                objectFit="cover"
                onError={() => setImageSrc(placeholderImage)}
                placeholder="blur"
                blurDataURL="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
              />
              {/* Play button */}
              {!isLoading && !playVideo && (
                <button
                  type="button"
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                  onClick={() => setIsLoading(true)}
                >
                  <Image
                    src={playIcon}
                    alt="Play icon"
                    width={92}
                    height={58}
                  />
                </button>
              )}
              {/* Loader */}
              {isLoading && (
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-accent-2">
                  <LoadingDots />
                </div>
              )}
            </div>
          )}
        {/* Start loading the iframe in the background if the user is previewing the diamond */}
        {previewed &&
          diamond.videourl &&
          hasValidVideoUrlFormat(diamond.videourl) && (
            <div className="md:block relative w-full h-full">
              <iframe
                className={cn(
                  'scale-[0.6] -translate-y-[6.72rem] -translate-x-6 md:translate-y-0 md:translate-x-0 left-0 md:scale-100 w-[210px] h-[210px] absolute top-0 md:top-[25px] md:left-[60px]',
                  { 'opacity-0': !isIframeAvailable && !playVideo },
                  { 'opacity-100': isIframeAvailable && playVideo }
                )}
                src={transformedVideoUrl(diamond.videourl, '210')}
                onLoad={() => setIsIframeAvailable(true)}
                allowFullScreen
              />
              <i className={cn('hidden md:block absolute left-2.5 bottom-1')}>
                <Rotate360 className="text-gray-dark" width="36" height="36" />
              </i>
            </div>
          )}
      </div>
      <DiamondAdditionalDetails diamond={diamond} />
    </div>
  )
}

export default DiamondPreview
